import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, XCircle, ArrowRight, Loader2 } from "lucide-react";
import { Logo } from "../components/Brand";
import { useApp, API } from "../AppContext";

export function BillingSuccess() {
  const { lang } = useApp();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling"); // polling | paid | expired | error
  const [tx, setTx] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let attempts = 0;
    const max = 8;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/billing/status/${sessionId}`);
        setTx(data);
        if (data.payment_status === "paid") {
          setStatus("paid");
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
        attempts += 1;
        if (attempts >= max) {
          setStatus("error");
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        attempts += 1;
        if (attempts >= max) setStatus("error");
        else setTimeout(poll, 2000);
      }
    };
    poll();
  }, [sessionId]);

  const T = lang === "fr" ? {
    poll: "Vérification de votre paiement...",
    poll_sub: "Ne fermez pas cette page.",
    paid_title: "Paiement réussi !",
    paid_sub: "Votre abonnement est maintenant actif. Bienvenue dans PROGÈRE Devis AI.",
    expired_title: "Session expirée",
    expired_sub: "La session de paiement a expiré. Veuillez réessayer.",
    error_title: "Statut de paiement inconnu",
    error_sub: "Si vous avez été débité, veuillez nous contacter ou rafraîchir la page.",
    cta_app: "Accéder à l'application",
    cta_retry: "Réessayer",
    plan: "Plan",
    amount: "Montant",
  } : {
    poll: "Verifying your payment...",
    poll_sub: "Do not close this page.",
    paid_title: "Payment successful!",
    paid_sub: "Your subscription is now active. Welcome to PROGÈRE Devis AI.",
    expired_title: "Session expired",
    expired_sub: "The checkout session has expired. Please try again.",
    error_title: "Unknown payment status",
    error_sub: "If you were charged, please contact us or refresh the page.",
    cta_app: "Go to dashboard",
    cta_retry: "Try again",
    plan: "Plan",
    amount: "Amount",
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 hero-grid-bg" style={{ background: "transparent" }} data-testid="billing-success-page">
      <div className="glass-strong w-full max-w-md p-10 text-center">
        <div className="mb-6 flex justify-center"><Logo size="md" /></div>
        {status === "polling" && (
          <>
            <Loader2 size={48} className="mx-auto mb-4 animate-spin" color="#D4AF37" />
            <h1 className="font-heading text-2xl font-bold mb-2" style={{ color: "#F5F5F5" }}>{T.poll}</h1>
            <p className="text-sm" style={{ color: "rgba(245,245,245,0.6)" }}>{T.poll_sub}</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 size={56} className="mx-auto mb-4" color="#4ADE80" />
            <h1 className="font-heading text-3xl font-bold mb-3" style={{ color: "#F5F5F5" }}>{T.paid_title}</h1>
            <p className="text-sm mb-6" style={{ color: "rgba(245,245,245,0.7)" }}>{T.paid_sub}</p>
            {tx && (
              <div className="text-left p-4 rounded-xl mb-6" style={{ background: "rgba(212,175,55,0.06)", border: "1px solid rgba(212,175,55,0.2)" }}>
                <div className="flex justify-between text-xs mb-2" style={{ color: "rgba(245,245,245,0.6)" }}>
                  <span>{T.plan}</span>
                  <span style={{ color: "#D4AF37" }}>{tx.plan_id === "pro" ? "Pro" : "Starter"}</span>
                </div>
                <div className="flex justify-between text-xs" style={{ color: "rgba(245,245,245,0.6)" }}>
                  <span>{T.amount}</span>
                  <span style={{ color: "#F5F5F5" }}>{tx.amount?.toFixed(2)} {tx.currency?.toUpperCase()}</span>
                </div>
              </div>
            )}
            <button onClick={() => nav("/app")} className="btn-red w-full justify-center" data-testid="success-go-app">
              {T.cta_app} <ArrowRight size={14} />
            </button>
          </>
        )}
        {status === "expired" && (
          <>
            <XCircle size={56} className="mx-auto mb-4" color="#C00000" />
            <h1 className="font-heading text-2xl font-bold mb-2" style={{ color: "#F5F5F5" }}>{T.expired_title}</h1>
            <p className="text-sm mb-6" style={{ color: "rgba(245,245,245,0.7)" }}>{T.expired_sub}</p>
            <Link to="/app/billing" className="btn-red w-full justify-center" data-testid="success-retry">{T.cta_retry}</Link>
          </>
        )}
        {status === "error" && (
          <>
            <XCircle size={56} className="mx-auto mb-4" color="#FFA500" />
            <h1 className="font-heading text-2xl font-bold mb-2" style={{ color: "#F5F5F5" }}>{T.error_title}</h1>
            <p className="text-sm mb-6" style={{ color: "rgba(245,245,245,0.7)" }}>{T.error_sub}</p>
            <button onClick={() => nav("/app")} className="btn-ghost w-full justify-center" data-testid="success-back">{T.cta_app}</button>
          </>
        )}
      </div>
    </div>
  );
}

export function BillingCancel() {
  const { lang } = useApp();
  const T = lang === "fr" ? {
    title: "Paiement annulé",
    sub: "Aucun montant n'a été débité. Vous pouvez réessayer à tout moment.",
    cta: "Retour à l'abonnement",
  } : {
    title: "Payment cancelled",
    sub: "No charge was made. You can try again anytime.",
    cta: "Back to subscription",
  };
  return (
    <div className="min-h-screen flex items-center justify-center px-6 hero-grid-bg" style={{ background: "transparent" }} data-testid="billing-cancel-page">
      <div className="glass-strong w-full max-w-md p-10 text-center">
        <div className="mb-6 flex justify-center"><Logo size="md" /></div>
        <XCircle size={56} className="mx-auto mb-4" color="rgba(245,245,245,0.5)" />
        <h1 className="font-heading text-2xl font-bold mb-2" style={{ color: "#F5F5F5" }}>{T.title}</h1>
        <p className="text-sm mb-6" style={{ color: "rgba(245,245,245,0.7)" }}>{T.sub}</p>
        <Link to="/app/billing" className="btn-red w-full justify-center" data-testid="cancel-retry">{T.cta}</Link>
      </div>
    </div>
  );
}
